import { PublicClientApplication } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import * as microsoftTeams from "@microsoft/teams-js";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { NotificationItem } from "../components-v2/notifications/types";
import { getClientAccessUrl } from "../utils/notification/NotificationHelperFunctions";

interface ClientURL {
  hubName: string;
  clientURL: string;
}

interface WebSocketResponse {
  clientURLs?: ClientURL[];
}

interface INotificationWebSocketContext {
  notifications: NotificationItem[];
  isConnected: boolean;
  error: string | null;
  removeNotifications?: (ids: string[]) => void;
}

interface INotificationWebSocketProviderProps {
  airport: string;
  children: React.ReactNode;
}

const defaultHubs = ["alerts", "notifications"];
const initialContext: INotificationWebSocketContext = {
  notifications: [],
  isConnected: false,
  error: null,
};

const NotificationWebSocketContext =
  createContext<INotificationWebSocketContext>(initialContext);

const parseNotification = (
  data: string,
  airport: string
): NotificationItem | null => {
  const message: unknown = JSON.parse(data);

  if (!message || typeof message !== "object") {
    return null;
  }

  const value = message as Record<string, unknown>;
  const requiredFields = [
    "id",
    "airportCode",
    "title",
    "body",
    "severity",
    "category",
    "timestamp",
  ];

  const hasInvalidStringField = requiredFields.some(
    (field) => typeof value[field] !== "string"
  );

  const hasInvalidUpdatedTimestamp =
    value.updatedTimestamp !== null &&
    typeof value.updatedTimestamp !== "string";

  if (hasInvalidStringField || hasInvalidUpdatedTimestamp) {
    return null;
  }

  if ((value.airportCode as string).toUpperCase() !== airport.toUpperCase()) {
    return null;
  }

  return {
    id: value.id as string,
    airportCode: value.airportCode as string,
    title: value.title as string,
    body: value.body as string,
    invalid: value.invalid as boolean,
    severity: value.severity as string,
    category: value.category as string,
    timestamp: value.timestamp as string,
    updatedTimestamp: value.updatedTimestamp as string | null,
    parameter: value.parameter as string,
    dismiss: value.dismiss as boolean,
    read: value.read as boolean,
    start: (value?.start || "") as string,
    end: (value?.end || "") as string,
  };
};

export const NotificationWebSocketProvider: React.FC<
  INotificationWebSocketProviderProps
> = ({ airport, children }) => {
  const msalInstance = useMsal().instance as PublicClientApplication;
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const removeNotifications = useCallback((ids: string[]) => {
    const idsToRemove = new Set(ids);
    setNotifications((current) =>
      current.filter((notification) => !idsToRemove.has(notification.id))
    );
  }, []);

  useEffect(() => {
    let isDisposed = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
    let reconnectAttempt = 0;
    let reconnectScheduled = false;
    let sockets: WebSocket[] = [];
    const connectedSockets = new Set<WebSocket>();

    setNotifications([]);
    setIsConnected(false);
    setError(null);

    if (!airport) {
      return () => {
        isDisposed = true;
      };
    }

    const closeSockets = () => {
      sockets.forEach((socket) => {
        socket.onopen = null;
        socket.onmessage = null;
        socket.onerror = null;
        socket.onclose = null;
        socket.close();
      });
      sockets = [];
      connectedSockets.clear();
    };

    const scheduleReconnect = (connect: () => Promise<void>) => {
      if (isDisposed || reconnectScheduled) {
        return;
      }

      reconnectScheduled = true;
      const delay = Math.min(1000 * 2 ** reconnectAttempt, 30000);
      reconnectAttempt += 1;
      reconnectTimer = setTimeout(() => {
        reconnectScheduled = false;
        void connect();
      }, delay);
    };

    const connect = async () => {
      closeSockets();

      try {
        const hubList = defaultHubs.map((hub) => `${hub}_${airport}`);
        const response = await getClientAccessUrl(
          microsoftTeams,
          msalInstance,
          hubList,
          airport
        );
        const { clientURLs = [] } = (response?.data || {}) as WebSocketResponse;

        if (isDisposed) {
          return;
        }
        if (clientURLs.length === 0) {
          throw new Error("No notification WebSocket URLs were returned");
        }

        sockets = clientURLs.map(({ clientURL, hubName }) => {
          const socket = new WebSocket(clientURL);

          socket.onopen = () => {
            connectedSockets.add(socket);
            reconnectAttempt = 0;
            setIsConnected(true);
            setError(null);
          };

          socket.onmessage = (event) => {
            try {
              const notification = parseNotification(event.data, airport);
              if (!notification) {
                setError("An invalid notification message was received");
                return;
              }

              setError(null);
              setNotifications((current) => {
                const notificationsWithoutCurrent = current.filter(
                  (item) => item.id !== notification.id
                );

                return notification.dismiss
                  ? notificationsWithoutCurrent
                  : [notification, ...notificationsWithoutCurrent];
              });
            } catch (_error) {
              setError("A notification message could not be parsed");
            }
          };

          socket.onerror = () => {
            setError(`Notification WebSocket error for ${hubName}`);
          };

          socket.onclose = () => {
            connectedSockets.delete(socket);
            if (connectedSockets.size === 0) {
              setIsConnected(false);
            }
            scheduleReconnect(connect);
          };

          return socket;
        });
      } catch (connectionError) {
        if (isDisposed) {
          return;
        }
        setIsConnected(false);
        setError(
          connectionError instanceof Error
            ? connectionError.message
            : "Unable to connect to notification WebSockets"
        );
        scheduleReconnect(connect);
      }
    };

    void connect();

    return () => {
      isDisposed = true;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
      }
      closeSockets();
    };
  }, [airport, msalInstance]);

  const value = useMemo(
    () => ({ notifications, isConnected, error, removeNotifications }),
    [notifications, isConnected, error, removeNotifications]
  );

  return (
    <NotificationWebSocketContext.Provider value={value}>
      {children}
    </NotificationWebSocketContext.Provider>
  );
};

export const useNotificationWebSocket = () =>
  useContext(NotificationWebSocketContext);
